<template>
  <div class="v2026-customer-panel">

    <PanelCollapseBarV2026 ariaLabel="ย่อแผงข้อมูลลูกค้า"
                           @toggleCollapse="$emit('toggleCollapse')" />

    <PanelTabBarV2026 :activeTab="activeTab" :labels="tabLabels" @setTab="setTab" />

    <PanelSubTabBarV2026 v-if="showSubTabBar"
                         :items="visibleSubTabs"
                         :activeKey="activeSubKey"
                         @select="goToSubTab" />

    <div class="v2026-cp-scroll" ref="subTabScroll">

    <!-- On every tab — the schedule tab is where a split account bites: bookings made under the
         other account never reach the linked account's calendar. "จัดการบัญชี" leads there. -->
    <div class="v2026-cp-multi-account" :class="{ 'is-above-header': activeTab === 1 }"
         v-if="otherAccounts.length" role="status">
      <!-- ย่อเป็นบรรทัดเดียวโดยค่าเริ่มต้น (จอ notebook โดนบัง) — กดเพื่อดูคำอธิบาย; "จัดการบัญชี" อยู่บรรทัดเดียวกันเสมอ -->
      <button type="button" class="v2026-cp-multi-account__toggle"
              :aria-expanded="multiAccountExpanded ? 'true' : 'false'"
              @click="multiAccountExpanded = !multiAccountExpanded">
        <!-- ไอคอนแทน emoji ⚠️ (emoji สี/ขนาดต่างกันตาม OS) — สีตาม --v2026-warning-fg ผ่าน currentColor -->
        <svg class="v2026-cp-multi-account__ico" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
                fill="currentColor"/>
          <path d="M12 9v4.5" fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round"/>
          <circle cx="12" cy="17" r="1.2" fill="#ffffff"/>
        </svg>
        <strong>พบบัญชีซ้ำ</strong>
        <span class="v2026-cp-multi-account__count">{{ otherAccounts.length }}</span>
        <!-- svg ตรง ๆ ไม่ใช้ <i class="fas">: Font Awesome JS แทน <i> ด้วย <svg> แล้ว :class ที่ bind ไว้หลุด -->
        <svg class="v2026-cp-multi-account__chev" :class="{ 'is-open': multiAccountExpanded }"
             viewBox="0 0 10 6" width="10" height="6" aria-hidden="true">
          <path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.5"
                stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      <button v-if="activeTab !== 3" type="button" class="v2026-cp-multi-account__link"
              @click="goToAccountManager">
        <i class="far fa-calendar-alt" aria-hidden="true"></i> จัดการบัญชี
      </button>
      <div class="v2026-cp-multi-account__detail" v-if="multiAccountExpanded">
        พบอีก {{ otherAccounts.length }} บัญชีที่เบอร์โทร/อีเมลตรงกัน การจองอาจอยู่ในบัญชีอื่นของคุณลูกค้าท่านนี้
      </div>
    </div>

    <div class="v2026-cp-tabpane" v-if="activeTab === 1">

    <PanelHeaderV2026 :type="0"
                      :statusIcons="headerStatusIcons"
                      :avatarUrl="avatarUrl"
                      :channelBadge="channelBadge"
                      :displayName="headerName"
                      roleLabel="คุณลูกค้า"
                      :subLink="latestBookingProvince"
                      :convCustomer="convCustomer"
                      :canEdit="!!convCustomer"
                      :conversationId="selectedConversation && selectedConversation.id"
                      :customerType="selectedConversation && selectedConversation.customer_type"
                      :professional="convCustomer && convCustomer.beneat_professional"
                      :beneatUserId="convCustomer && convCustomer.beneat_user_id"
                      :beneatUserHref="headerUserHref"
                      @notify="showToast($event.variant, $event.title, $event.message)" />

    <important-message-v2026 :customer="convCustomer"
      :conversationId="selectedConversation && selectedConversation.id" />

    <UserInfoCardV2026 :type="0"
                       :idValue="userId"
                       :phone="phone"
                       :email="email"
                       :hasEmail="true"
                       :convCustomer="convCustomer"
                       :canEdit="!!convCustomer"
                       :conversationId="selectedConversation && selectedConversation.id"
                       :noAccount="noAccount"
                       @notify="showToast($event.variant, $event.title, $event.message)" />

    <TagsSectionV2026 :type="0" :selectedConversation="selectedConversation"
                      :tags="tags" :admin="admin"
                      @notify="showToast($event.variant, $event.title, $event.message)" />

    <div class="v2026-cp-system">
      <span class="v2026-cp-section-label">ข้อมูลลูกค้าในระบบ</span>
      <EntityCardV2026 variant="card"
                       :avatarUrl="avatarUrl"
                       :displayName="systemName"
                       idLabel="User ID:"
                       :idValue="userId"
                       :idHref="linkProfileUser(userId)" />
    </div>

    <!-- TODO(wiring): these tiles are still placeholders. -->
    <div class="v2026-cp-stats">
      <!-- A stat with a hint shows it on hover anywhere on the tile; the info glyph only signals it. -->
      <div class="v2026-cp-card v2026-cp-stat" v-for="stat in stats" :key="stat.label"
           :class="{ 'v2026-cp-stat--wide': stat.wide }"
           v-b-tooltip.hover.top :title="stat.hint || null">
        <div class="v2026-cp-stat__top">
          <span v-if="!['coin'].includes(stat.label.toLowerCase())" class="v2026-cp-mask v2026-cp-stat__ico" aria-hidden="true"
                :style="maskStyle(stat.icon)"></span>
          <img v-else class="v2026-cp-stat__ico" style="border-radius: 50%;" :src="stat.icon" :alt="stat.label">
          <span class="v2026-cp-stat__label" >{{ stat.label }}</span>
          <i v-if="stat.hint" class="fas fa-info-circle v2026-cp-stat__hint" role="img"
             :aria-label="stat.hint"></i>
          <!-- Only the arrow and the value open the link, not the whole tile. -->
          <a v-if="stat.link" class="v2026-cp-stat__link" :href="stat.link" target="_blank" rel="noopener"
             :aria-label="`เปิด ${stat.label}`">
            <span class="v2026-cp-mask v2026-cp-stat__link-ico" aria-hidden="true"
                  :style="maskStyle(icons.chevronRight)"></span>
          </a>
        </div>
        <!-- Renders as <a> only when there is somewhere to go, so a linkless stat keeps its
             plain <div> rather than becoming an anchor with no href. -->
        <component :is="stat.link ? 'a' : 'div'" class="v2026-cp-stat__value"
                   :class="{ 'v2026-cp-stat__value--link': stat.link }"
                   v-bind="stat.link ? { href: stat.link, target: '_blank', rel: 'noopener' } : {}">{{ stat.value }}</component>
        <div v-if="stat.requests && stat.requests.length" class="v2026-cp-stat__requests">
          <a v-for="req in stat.requests" :key="req.id" class="v2026-cp-stat__request"
             :href="req.href" target="_blank" rel="noopener">#{{ req.id }}</a>
          <span v-if="stat.moreRequests" class="v2026-cp-stat__request-more">+{{ stat.moreRequests }}</span>
        </div>
      </div>
    </div>

    <div class="v2026-cp-accounts" v-if="otherAccounts.length">
      <div class="v2026-cp-section-head">
        <span class="v2026-cp-section-label">Account ที่ใช้เบอร์โทร/อีเมลเดียวกัน</span>
        <!-- Directive on the wrapper: Font Awesome's JS swaps the <i> for an <svg>. -->
        <span class="v2026-cp-hint" role="img" tabindex="0"
              aria-label="คำอธิบายบัญชีที่ใช้เบอร์โทร/อีเมลเดียวกัน และการสลับบัญชี"
              v-b-tooltip.hover.focus.left.html="{ title: otherAccountsHint, customClass: 'v2026-cp-tip' }">
          <i class="fas fa-info-circle" aria-hidden="true"></i>
        </span>
      </div>
      <div class="v2026-cp-accounts__list">
        <EntityCardV2026 v-for="acc in otherAccounts" :key="acc.id"
                         variant="row"
                         :avatarUrl="userAvatar(acc.picture)"
                         :displayName="acc.full_name"
                         idLabel="User ID:"
                         :idValue="acc.id"
                         :idHref="linkProfileUser(acc.id)"
                         :tags="matchReasonLabels(acc.match_reasons)" />
      </div>
    </div>

    </div><!-- /TAB 1 -->

    <div class="v2026-cp-tabpane v2026-cp-notes" v-if="activeTab === 2">

      <SystemNotesV2026 ref="secSystemNotes" :type="0" :entityId="userId" :admin="admin" />

      <ChatNoteV2026 ref="secChatNote" :type="0" :customer="convCustomer"
                     :conversationId="selectedConversation && selectedConversation.id"
                     @notify="showToast($event.variant, $event.title, $event.message)" />

    </div><!-- /TAB 2 -->

    <div class="v2026-cp-tabpane v2026-cp-bookings" v-if="activeTab === 3">

      <!-- Sub-tabs "ตารางคุณลูกค้า" / "ตารางคุณแม่บ้าน" scroll-spy over these two stacked sections, like
           tab 2. The customer section stays first so the maid section's bottom padding ends the pane. -->
      <div v-if="useCustomerScheduleSubTabs" ref="secCustomerSchedule">
        <CustomerScheduleV2026 :userId="userId" :accounts="scheduleAccounts"
                               :canSwitch="!!(convCustomer && selectedConversation)"
                               @switch="openSwitchById" />
      </div>

      <div ref="secMaidSchedule" :class="{ 'v2026-cp-schedsec--divided': useCustomerScheduleSubTabs }">
        <schedule-tab-v2026 :type="0"
          :professionals="professionals" :selected-maid="selectedMaid" :schedule-month="scheduleMonth"
          :orders-by-day-map="ordersByDayMap" :selected-schedule-day="selectedScheduleDay"
          :schedule-loading="scheduleLoading" :selected-day-orders="selectedDayOrders"
          :latest-order="latestOrder" :use-calendar-component="useCalendarComponent"
          :icons="icons" :weekdays="weekdays" :calendar-cells="calendarCells" :thai-month-label="thaiMonthLabel"
          @search="onSearchMaid" @select-maid="onSelectMaid" @month-change="onCalMonthChange"
          @select-day="onCalSelectDay" @create="createBooking"
          @prev-month="prevMonth" @next-month="nextMonth" @select-cell="selectScheduleDay" />
      </div>

    </div><!-- /TAB 3 -->

    <div class="v2026-cp-tabpane" v-if="activeTab === 4">
      <AiAssistantTabV2026 :selected-conversation="selectedConversation"
                           empty-text="• ไม่พบข้อมูลการสนทนาของลูกค้า"
                           :showFunctionCalling="canSeeFunctionCalling" />
    </div><!-- /TAB 4 -->

    </div><!-- /v2026-cp-scroll -->

    <BaseToast :open="toast.open" :variant="toast.variant" :title="toast.title" :message="toast.message"
               :icon="toast.icon"
               @close="toast.open = false" />

    <BaseModal :open="!!switchTarget" max-width="440px"
               :position="['top', 'center']" :modalStyle="['margin-top:10%']"
               @close="closeSwitchConfirm()">
      <div v-if="switchTarget" class="v2026-cp-swm" role="dialog" aria-modal="true"
           aria-labelledby="v2026-cp-swm-title">
        <div class="v2026-cp-swm__head">
          <span id="v2026-cp-swm-title" class="v2026-cp-swm__title">ผูกแชทกับบัญชีที่มีการจอง</span>
          <button type="button" class="v2026-cp-swm__close" aria-label="ปิด"
                  :disabled="switching" @click="closeSwitchConfirm()">×</button>
        </div>
        <div class="v2026-cp-swm__body">
          <div class="v2026-cp-swm__route">
            <div class="v2026-cp-swm__acc">
              <span class="v2026-cp-swm__acc-label">จาก</span>
              <div class="v2026-cp-swm__acc-row">
                <img class="v2026-cp-swm__avatar" :src="userAvatar((cust && cust.picture) || null)" alt="">
                <div class="v2026-cp-swm__acc-text">
                  <span class="v2026-cp-swm__acc-id">ID {{ userId }}</span>
                  <span class="v2026-cp-swm__acc-name">{{ (cust && cust.full_name) || '-' }}</span>
                </div>
              </div>
            </div>
            <span class="v2026-cp-swm__arrow" aria-hidden="true">→</span>
            <div class="v2026-cp-swm__acc is-target">
              <span class="v2026-cp-swm__acc-label">เป็น</span>
              <div class="v2026-cp-swm__acc-row">
                <img class="v2026-cp-swm__avatar" :src="userAvatar(switchTarget.picture || null)" alt="">
                <div class="v2026-cp-swm__acc-text">
                  <span class="v2026-cp-swm__acc-id">ID {{ switchTarget.id }}</span>
                  <span class="v2026-cp-swm__acc-name">{{ switchTarget.full_name || '-' }}</span>
                </div>
              </div>
            </div>
          </div>
          <ul class="v2026-cp-swm__notes">
            <li>ข้อมูลลูกค้า ตารางการจอง และ AI ในแชทนี้จะใช้บัญชี ID {{ switchTarget.id }}</li>
            <li>ระบบจะไม่เปลี่ยนกลับให้อัตโนมัติ กด "สลับใช้บัญชีนี้" ที่บัญชีเดิมเพื่อย้อนกลับได้</li>
            <li>ไม่กระทบบัญชีลูกค้าในระบบ BeNeat — การล็อกอิน LINE ของลูกค้ายังเหมือนเดิม</li>
          </ul>
          <p v-if="switchError" class="v2026-cp-swm__error" role="alert">{{ switchError }}</p>
          <div class="v2026-cp-swm__actions">
            <button type="button" class="v2026-cp-swm__cancel" :disabled="switching"
                    @click="closeSwitchConfirm()">ยกเลิก</button>
            <button type="button" class="v2026-cp-swm__confirm" :disabled="switching"
                    @click="confirmSwitch()">{{ switching ? 'กำลังเปลี่ยน…' : 'ยืนยันเปลี่ยนบัญชี' }}</button>
          </div>
        </div>
      </div>
    </BaseModal>

  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import { mapState, mapActions } from 'vuex'
import ScheduleTabV2026 from './ScheduleTabV2026.vue'
import BaseToast from '@/components/coversation/BaseToast.vue'
import BaseModal from '@/components/coversation/BaseModal.vue'
import ImportantMessageV2026 from '@/components/coversation/ImportantMessageV2026.vue'
import AiAssistantTabV2026 from '@/components/coversation/AiAssistantTabV2026.vue'
import PanelCollapseBarV2026 from '@/components/coversation/PanelCollapseBarV2026.vue'
import PanelTabBarV2026 from '@/components/coversation/PanelTabBarV2026.vue'
import PanelSubTabBarV2026 from '@/components/coversation/PanelSubTabBarV2026.vue'
import panelSubTabs2026 from '@/components/coversation/panelSubTabs2026'
import railFunctionCalling2026 from '@/components/coversation/railFunctionCalling2026'
import SystemNotesV2026 from '@/components/coversation/SystemNotesV2026.vue'
import TagsSectionV2026 from '@/components/coversation/TagsSectionV2026.vue'
import ChatNoteV2026 from '@/components/coversation/ChatNoteV2026.vue'
import PanelHeaderV2026 from '@/components/coversation/PanelHeaderV2026.vue'
import UserInfoCardV2026 from '@/components/coversation/UserInfoCardV2026.vue'
import EntityCardV2026 from '@/components/coversation/EntityCardV2026.vue'
import { resolveStatusIcons } from '@/components/coversation/statusIconCatalog'
import CustomerScheduleV2026 from '@/components/coversation/CustomerScheduleV2026.vue'

// Why an account is in "Account ที่ใช้เบอร์โทร/อีเมลเดียวกัน" — codes from chat-server
// userHelper.searchOtherBeneatAccounts. The *_account ones name the id of the account this
// panel shows, so the admin sees exactly which account it matched.
// Admin-facing explanation for the other-accounts section. Static markup only (rendered as tooltip
// HTML) — never interpolate customer data here.
const OTHER_ACCOUNTS_HINT = [
  '<strong>บัญชีในรายการนี้</strong>',
  '• บัญชี BeNeat อื่นที่เบอร์โทรหรืออีเมลตรงกับแชทนี้ หรือกับบัญชีที่แสดงอยู่ด้านบน — อาจเป็นลูกค้าคนเดียวกันที่มีหลายบัญชี',
  '• ป้ายใต้ ID บอกว่าตรงกันด้วยอะไร เช่น "เบอร์ตรงกับแชท", "อีเมลตรงกับ ID …", "LINE ตรงกับแชท" (บัญชีที่ LINE ของแชทผูกอยู่)',
  '<strong>ดูการจอง / สลับบัญชี</strong>',
  '• กด "จัดการบัญชี" ที่แถบเตือนด้านบน เพื่อไปที่ตารางคุณลูกค้า',
  '• กดที่การ์ดบัญชี = ดูการจองของบัญชีนั้น (ดูอย่างเดียว ไม่เปลี่ยนการผูกแชท)',
  '• กด "สลับใช้บัญชีนี้" = ให้แชทนี้ผูกกับบัญชีนั้น — ข้อมูลลูกค้า ตารางการจอง บอท AI และ AI assistant ในแชทนี้จะใช้บัญชีนั้น ระบบจะไม่เปลี่ยนกลับเอง',
  '• ไม่กระทบบัญชีลูกค้าในระบบ BeNeat: ลูกค้ายังล็อกอิน LINE เข้าบัญชีเดิม และการจองที่ลูกค้าทำเองผ่าน LINE จะเข้าบัญชีที่ LINE ผูกอยู่'
].join('<br>')

const MATCH_REASON_LABELS = {
  line_chat: () => 'LINE ตรงกับแชท',
  phone_chat: () => 'เบอร์ตรงกับแชท',
  phone_account: id => `เบอร์ตรงกับ ID ${id}`,
  email_chat: () => 'อีเมลตรงกับแชท',
  email_account: id => `อีเมลตรงกับ ID ${id}`
}

// Tab 3 split into "ตารางคุณลูกค้า" + "ตารางคุณแม่บ้าน". false = tab 3 exactly as before (maid schedule only).
const USE_CUSTOMER_SCHEDULE_SUBTABS = true
// The "การจองบริการล่าสุด" card is rendered inside ScheduleTabV2026, so it can't be a $ref here.
const LATEST_ORDER_ANCHOR = 'secLatestOrder'

// One-shot per press: a collapse/expand remount must not jump back to the AI tab.
let appliedVisionToken = null

export default {
  name: 'customer-panel-v2026',
  mixins: [panelSubTabs2026, railFunctionCalling2026],
  components: {
    ScheduleTabV2026,
    BaseToast,
    BaseModal,
    ImportantMessageV2026,
    AiAssistantTabV2026,
    PanelCollapseBarV2026,
    PanelTabBarV2026,
    PanelSubTabBarV2026,
    SystemNotesV2026,
    TagsSectionV2026,
    ChatNoteV2026,
    PanelHeaderV2026,
    UserInfoCardV2026,
    EntityCardV2026,
    CustomerScheduleV2026
  },
  props: {
    selectedConversation: {
      type: Object,
      default: null
    },
    customer: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      activeTab: 1,
      // แถบ "พบบัญชีซ้ำ" ย่อไว้ก่อน — เปลี่ยนแชทแล้วกลับมาย่อ
      multiAccountExpanded: false,
      subTabScrollRef: 'subTabScroll',
      // A tab with no entry gets no strip and no spy work.
      subTabs: Object.assign({
        2: [
          { key: 'sysnote', label: 'โน้ตในระบบ', refs: ['secSystemNotes'] },
          { key: 'chatnote', label: 'โน้ตแชท', refs: ['secChatNote'] }
        ]
      }, USE_CUSTOMER_SCHEDULE_SUBTABS ? {
        3: [
          { key: 'custsched', label: 'ตารางคุณลูกค้า', refs: ['secCustomerSchedule'] },
          { key: 'maidsched', label: 'ตารางคุณแม่บ้าน', refs: ['secMaidSchedule'] },
          // Not a $ref of this panel — resolveSubTabEl finds it inside the maid section.
          { key: 'latestorder', label: 'การจองล่าสุด', refs: [LATEST_ORDER_ANCHOR] }
        ]
      } : {}),
      useCustomerScheduleSubTabs: USE_CUSTOMER_SCHEDULE_SUBTABS,
      tabLabels: {
        tab1: 'ข้อมูลลูกค้า',
        tab2: 'โน้ต',
        tab3: 'บุ๊กมาร์ก',
        tab4: 'ผู้ช่วย AI'
      },
      icons: {
        bookmark: require('@/assets/icons2026/bookmark-03.svg'),
        edit: require('@/assets/icons2026/pencil-edit.svg'),
        chevron: require('@/assets/icons2026/arrow-right.svg'),
        line: require('@/assets/icons2026/line-color.svg'),
        facebook: require('@/assets/icons2026/facebook-color.svg'),
        coin: require('@/assets/icons2026/beneat-coin.svg'),
        point: require('@/assets/icons2026/point.svg'),
        discountCode: require('@/assets/icons2026/discount.svg'),
        booking: require('@/assets/icons2026/booking.svg'),
        review: require('@/assets/icons2026/c-review.svg'),
        rating: require('@/assets/icons2026/c-rating.svg'),
        quoteRequest: require('@/assets/icons2026/document.svg'),
        externalLink: require('@/assets/icons2026/external-link.svg'),
        trash: require('@/assets/icons2026/trash.svg'),
        chevronLeft: require('@/assets/icons2026/chevron-left.svg'),
        chevronRight: require('@/assets/icons2026/chevron-right.svg'),
        clock: require('@/assets/icons2026/clock.svg'),
        bClock: require('@/assets/icons2026/b-time.svg'),
        calendar: require('@/assets/icons2026/calendar.svg'),
        bCalendar: require('@/assets/icons2026/b-calendar.svg'),
        bLocation: require('@/assets/icons2026/b-location.svg'),
        bMapLocation: require('@/assets/icons2026/b-maps-location.svg'),
        createBooking: require('@/assets/icons2026/create-booking.svg')
      },
      weekdays: ['อา.', 'จ.', 'อ.', 'พ.', 'พฤ.', 'ศ.', 'ส.'],
      thaiMonths: ['มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน', 'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'],
      scheduleMonth: moment().startOf('month'), // viewed month
      useCalendarComponent: true,
      selectedMaid: null, // chosen professional (from searchProfessionals)
      maidOrders: [], // orders for scheduleMonth
      scheduleLoading: false, // orders GET in flight
      selectedScheduleDay: null, // 'YYYY-MM-DD' of the clicked calendar cell
      placeholderAvatar: 'https://beneat.co/static/img/unknown.png',
      toast: { open: false, variant: 'success', title: '', message: '' },
      otherAccountsHint: OTHER_ACCOUNTS_HINT,
      switchTarget: null, // the other account being confirmed for "switch"
      switching: false,
      switchError: ''
    }
  },
  computed: {
    ...mapState('admin', ['admin']),
    ...mapState('tag', ['tags']),
    ...mapState('professional', ['professionals']),
    ...mapState('aiAssistant', ['visionRequest']),
    thaiMonthLabel () {
      return `${this.thaiMonths[this.scheduleMonth.month()]} ${this.scheduleMonth.year()}`
    },
    ordersByDayMap () {
      const map = {}
      this.maidOrders.forEach(o => {
        if (!o.cleaning_date) return
        const key = moment(o.cleaning_date).format('YYYY-MM-DD')
        if (!map[key]) map[key] = []
        map[key].push(o)
      })
      return map
    },
    calendarCells () {
      const start = this.scheduleMonth.clone().startOf('month')
      const daysInMonth = start.daysInMonth()
      const leading = start.day() // 0=Sun … blanks before day 1
      const map = this.ordersByDayMap
      const cells = []
      for (let i = 0; i < leading; i++) {
        cells.push({ blank: true, key: 'blank-' + i })
      }
      for (let d = 1; d <= daysInMonth; d++) {
        const dateStr = start.clone().date(d).format('YYYY-MM-DD')
        cells.push({ blank: false, key: dateStr, day: d, dateStr, count: (map[dateStr] || []).length })
      }
      return cells
    },
    selectedDayOrders () {
      if (!this.selectedScheduleDay) return []
      return this.ordersByDayMap[this.selectedScheduleDay] || []
    },
    cust () {
      return (this.customer && this.customer.customer) ? this.customer.customer : null
    },
    convCustomer () {
      return (this.selectedConversation && this.selectedConversation.customer)
        ? this.selectedConversation.customer : null
    },
    // Reuses this panel's OWN linkProfileUser — no second copy of the admin base URL. Guarded on
    // the id so a missing one yields null, never ".../user/undefined".
    headerUserHref () {
      const id = this.convCustomer && this.convCustomer.beneat_user_id
      return id ? this.linkProfileUser(id) : null
    },
    avatarUrl () {
      const social = this.convCustomer && this.convCustomer.profile_picture
      if (social) return social
      const pic = this.cust && this.cust.picture
      if (pic && /^(https?:)?\/\//.test(pic)) return pic
      return this.placeholderAvatar
    },
    // Same icons the chat card puts before the name.
    headerStatusIcons () {
      const ids = this.selectedConversation && this.selectedConversation.status_icons
      return resolveStatusIcons(Array.isArray(ids) ? ids : [])
    },
    headerName () {
      const n = this.convCustomer && this.convCustomer.name
      if (n && String(n).trim()) return n
      return this.systemName
    },
    systemName () {
      return (this.cust && this.cust.full_name) ? this.cust.full_name : '-'
    },
    userId () {
      return this.cust ? this.cust.id : null
    },
    // Search finished and found no BeNeat user for this LINE customer → the empty User ID
    // reads "ยังไม่มีบัญชี" (the register invite itself is sent from the composer's Mini App menu).
    noAccount () {
      const conv = this.selectedConversation
      return !!this.customer && this.userId === null &&
        !!conv && conv.type === 'LineAtConversation' && conv.customer_type === 'customer'
    },
    phone () {
      return this.convCustomer ? this.convCustomer.phone : '-'
    },
    // Same source as phone and as the edit-contact modal's v-model (legacy CustomerInformation.vue
    // also shows selectedConversation.customer.email) — not the system user's cust.email.
    email () {
      return this.convCustomer ? this.convCustomer.email : null
    },
    // Quote-request list filtered to this customer: the email shown in the user-info card
    // first, else the system user's email; no email → the unfiltered list.
    quoteRequestLink () {
      const base = 'https://admin-test.beneat.co/deep-cleaning-service?tab=quote-request'
      const email = [this.email, this.cust && this.cust.email]
        .map(e => (e ? String(e).trim() : ''))
        .find(Boolean)
      return email ? `${base}&email=${encodeURIComponent(email)}` : base
    },
    // Old-system service requests, each linked to its admin detail page.
    legacyServiceRequests () {
      const ids = (this.cust && Array.isArray(this.cust.legacy_service_request_ids))
        ? this.cust.legacy_service_request_ids : []
      return ids.map(id => ({ id, href: `https://admin-test.beneat.co/service-requests/${id}/detail` }))
    },
    latestBookingProvince () {
      const province = this.cust && this.cust.latest_order && this.cust.latest_order.province
        ? this.cust.latest_order.province.name_th : null
      return province || '-'
    },
    latestOrder () {
      return (this.cust && this.cust.latest_order) ? this.cust.latest_order : null
    },
    otherAccounts () {
      return (this.customer && Array.isArray(this.customer.other_accounts))
        ? this.customer.other_accounts : []
    },
    // The calendar's account list: the linked account ("ปัจจุบัน") first, then the other accounts.
    scheduleAccounts () {
      if (!this.userId) return []
      const row = (acc, isCurrent) => ({
        id: acc.id,
        name: acc.full_name || null,
        avatarUrl: this.userAvatar(acc.picture || null),
        href: this.linkProfileUser(acc.id),
        isCurrent
      })
      return [
        row(Object.assign({ id: this.userId }, this.cust), true),
        ...this.otherAccounts.map(acc => row(acc, false))
      ]
    },
    channelBadge () {
      const type = this.selectedConversation ? this.selectedConversation.type : null
      switch (type) {
        case 'LineAtConversation':
        case 'LineProfessionalConversation':
          return { icon: this.icons.line, alt: 'LINE' }
        case 'PublicConversation':
          return { icon: this.icons.facebook, alt: 'Facebook' }
        default:
          return null
      }
    },
    stats () {
      const c = this.cust || {}
      return [
        { label: 'Coin', value: c.total_credits ? parseFloat(c.total_credits) : 0, icon: this.icons.coin },
        { label: 'Point', value: c.total_points ? parseFloat(c.total_points) : 0, icon: this.icons.point },
        { label: 'โค้ดส่วนลด', value: (c.promo_code && c.promo_code.length > 0) ? c.promo_code.length : 0, icon: this.icons.discountCode },
        { label: 'Booking', value: c.beneat_order_qty ? Math.round(c.beneat_order_qty) : 0, icon: this.icons.bookmark },
        {
          label: 'Review',
          value: c.review_qty ? Math.round(c.review_qty) : 0,
          icon: this.icons.review,
          // review_qty = COUNT(tb_professional_rating) over this user's orders (chat-server searchUser).
          hint: 'จำนวนครั้งที่ลูกค้าคนนี้ให้คะแนนแม่บ้าน (นับทุกครั้ง ทั้งที่เขียนและไม่เขียนคอมเมนต์)'
        },
        {
          label: 'Rating',
          value: c.avg_rating ? parseFloat(c.avg_rating).toFixed(1) : 0,
          icon: this.icons.rating,
          // avg_rating = AVG(tb_professional_rating.rating) over this user's orders (chat-server searchUser).
          hint: 'คะแนนเฉลี่ยที่ลูกค้าคนนี้ให้แม่บ้าน จากทุกออเดอร์ที่เคยให้คะแนน (ไม่ใช่คะแนนที่แม่บ้านให้ลูกค้า)'
        },
        {
          label: 'คำขอประเมินราคา (ใหม่)',
          // quote_request_qty = open tb_support_ticket (status 6–10) of this user (chat-server searchUser).
          value: c.quote_request_qty ? Math.round(c.quote_request_qty) : 0,
          icon: this.icons.quoteRequest,
          // Spans both columns so the long Thai label stays on one line.
          wide: true,
          link: this.quoteRequestLink
        },
        {
          label: 'คำขอ Big Cleaning (ระบบเก่า)',
          // legacy_service_request_* = non-deleted tb_service_request of this user, newest first
          // (chat-server searchUser returns the count plus the latest few ids).
          value: c.legacy_service_request_qty ? Math.round(c.legacy_service_request_qty) : 0,
          icon: this.icons.quoteRequest,
          wide: true,
          requests: this.legacyServiceRequests,
          moreRequests: Math.max(0, (c.legacy_service_request_qty || 0) - this.legacyServiceRequests.length)
        }
      ]
    }
  },
  watch: {
    visionRequest () {
      this.applyVisionRequest()
    },
    'selectedConversation.id' () {
      this.multiAccountExpanded = false
    }
  },
  // A request written before this panel mounted (collapsed column) never fires the watcher.
  mounted () {
    this.applyVisionRequest()
  },
  methods: {
    // Pressing "ให้ AI วิเคราะห์รูป" in the chat lands the admin on the AI tab straight away.
    applyVisionRequest () {
      const request = this.visionRequest
      if (!request || request.token === appliedVisionToken) return
      appliedVisionToken = request.token
      this.activeTab = 4
    },
    showToast (variant, title, message) {
      this.toast = { open: true, variant, title, message }
    },
    userAvatar (picture) {
      return picture !== null ? picture : 'https://beneat.co/static/img/unknown.png'
    },
    linkProfileUser (userId) {
      return userId !== null ? `https://admin-test.beneat.co/user/${userId}` : null
    },
    setTab (n) {
      this.activeTab = n
    },
    matchReasonLabels (reasons) {
      return (Array.isArray(reasons) ? reasons : [])
        .filter(reason => MATCH_REASON_LABELS[reason])
        .map(reason => MATCH_REASON_LABELS[reason](this.userId))
    },
    // "จัดการบัญชี": the account list (view / switch) lives on the customer calendar in tab 3 —
    // switch there first, then jump to that sub-section once it renders.
    goToAccountManager () {
      this.setTab(3)
      this.$nextTick(() => {
        if (this.useCustomerScheduleSubTabs) this.goToSubTab('custsched')
      })
    },
    // The calendar's "สลับใช้บัญชีนี้" sends an id; the modal needs the full other-account row.
    openSwitchById (id) {
      const acc = this.otherAccounts.find(other => other.id === id)
      if (acc) this.openSwitchConfirm(acc)
    },
    ...mapActions('professional', ['searchProfessionals']),
    ...mapActions('conversation', ['searchCustomer']),
    openSwitchConfirm (acc) {
      this.switchError = ''
      this.switchTarget = acc
    },
    closeSwitchConfirm () {
      if (this.switching) return
      this.switchTarget = null
      this.switchError = ''
    },
    // Re-links this chat to the picked account (chat-server customerController.switchBeneatUser),
    // then re-runs search-user: it reads the new link first, so the header, stats, calendar and
    // the "same phone/email" list all move to that account — the old one lands in that list.
    async confirmSwitch () {
      const c = this.convCustomer
      const conv = this.selectedConversation
      const target = this.switchTarget
      if (!c || !conv || !target || this.switching) return
      this.switching = true
      this.switchError = ''
      try {
        const { data } = await axios.post(`/customers/${c.id}/switch-beneat-user`, {
          beneat_user_id: target.id,
          admin_id: this.admin && this.admin.id,
          conversation_id: conv.id
        })
        c.beneat_user_id = (data && data.customer && data.customer.beneat_user_id) || target.id
        this.switching = false
        this.switchTarget = null
        await this.searchCustomer({
          phone: c.phone,
          email: c.email,
          line_user_id: c.lid,
          customer_id: c.id,
          beneat_professional_id: c.beneat_professional_id
        })
        this.showToast('success', 'เปลี่ยนบัญชีสำเร็จ', `แชทนี้ผูกกับบัญชี ID ${target.id} แล้ว`)
      } catch (e) {
        this.switching = false
        const msg = e && e.response && e.response.data && e.response.data.message
        this.switchError = msg ? `เปลี่ยนบัญชีไม่สำเร็จ: ${msg}` : 'เปลี่ยนบัญชีไม่สำเร็จ กรุณาลองใหม่'
      }
    },
    async onSearchMaid (search, loading) {
      if (!search || !search.trim()) return
      loading(true)
      try {
        await this.searchProfessionals(search)
      } finally {
        loading(false)
      }
    },
    onSelectMaid (maid) {
      this.selectedMaid = maid
      this.selectedScheduleDay = null
      this.maidOrders = []
      if (maid) this.fetchSchedule()
    },
    async fetchSchedule () {
      if (!this.selectedMaid) return
      this.scheduleLoading = true
      try {
        const month = this.scheduleMonth.format('YYYY-MM')
        const { data } = await axios.get(`/beneat/professionals/${this.selectedMaid.id}/orders`, { params: { month } })
        this.maidOrders = (data && Array.isArray(data.data)) ? data.data : []
      } catch (e) {
        this.maidOrders = []
      } finally {
        this.scheduleLoading = false
      }
    },
    prevMonth () {
      this.scheduleMonth = this.scheduleMonth.clone().subtract(1, 'month')
      this.selectedScheduleDay = null
      this.fetchSchedule()
    },
    nextMonth () {
      this.scheduleMonth = this.scheduleMonth.clone().add(1, 'month')
      this.selectedScheduleDay = null
      this.fetchSchedule()
    },
    selectScheduleDay (cell) {
      this.selectedScheduleDay = cell.dateStr
    },
    onCalMonthChange (ym) {
      this.scheduleMonth = moment(ym, 'YYYY-MM').startOf('month')
      this.selectedScheduleDay = null
      this.fetchSchedule()
    },
    onCalSelectDay (dateStr) {
      this.selectedScheduleDay = dateStr
    },
    createBooking () {
      const params = { auto_fill: 1 }
      if (this.convCustomer && this.convCustomer.id) params.user_id = this.convCustomer.id
      if (this.selectedMaid) params.selected_professional_id = this.selectedMaid.id
      if (this.selectedScheduleDay) params.service_date = this.selectedScheduleDay
      const qs = new URLSearchParams(params).toString()
      let url = `https://admin-test.beneat.co/order/create-multiple?${qs}`
      window.open(url, '_blank')
    },
    maskStyle (url) {
      return {
        '-webkit-mask-image': 'url(' + url + ')',
        'mask-image': 'url(' + url + ')'
      }
    },
    // Overrides the mixin's resolver for the one anchor that lives inside a child component; every
    // other ref still goes through the shared implementation. The card only exists while the
    // customer has a latest order, so the sub-tab hides itself otherwise (mixin presence rule).
    resolveSubTabEl (refName) {
      if (refName !== LATEST_ORDER_ANCHOR) {
        return panelSubTabs2026.methods.resolveSubTabEl.call(this, refName)
      }
      const host = this.$refs.secMaidSchedule
      const el = host && host.querySelector ? host.querySelector('[data-subtab-anchor="latest-order"]') : null
      return (el && el.isConnected) ? el : null
    }
  }
}
</script>

<style scoped>
.v2026-customer-panel {
  --v2026-color-main: #31C1D7;
  --v2026-color-main-soft: #31C1D714;
  --v2026-color-icon-idle: #66696C;

  --v2026-link: #007bff;

  --v2026-surface: #ffffff;
  --v2026-surface-sunken: #F8FAFC;
  --v2026-surface-hover: #e9ecf1;

  --v2026-border: #e4e7ec;
  --v2026-text: #101828;
  --v2026-text-secondary: #667085;
  --v2026-text-muted: #98a2b3;
  --v2026-icon: #475467;

  --v2026-warning-fg: #B54708;
  --v2026-warning-bg: #FFFAEB;
  --v2026-warning-bd: #FEDF89;

  --v2026-r-card: 12px;
  --v2026-r-box: 14px;
  --v2026-r-tab: 10px;

  --v2026-sp-1: 4px;
  --v2026-sp-2: 8px;
  --v2026-sp-3: 12px;
  --v2026-sp-4: 16px;
  --v2026-sp-5: 20px;
  --v2026-sp-6: 24px;

  --v2026-fs-section: 13px;
  --v2026-fs-body: 14px;
  --v2026-fs-meta: 12px;
  --v2026-fs-name: 16px;

  display: flex;
  flex-direction: column;
  gap: var(--v2026-sp-4);
  height: 100%;
  overflow: hidden;
  background: var(--v2026-surface);
  padding: 0;
}

.v2026-cp-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding-bottom: var(--v2026-sp-6);
}

.v2026-cp-mask {
  display: inline-block;
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
  -webkit-mask-size: contain;
          mask-size: contain;
  background-color: var(--v2026-icon);
}

.v2026-cp-section-label {
  font-size: var(--v2026-fs-section);
  font-weight: 600;
  color: var(--v2026-text-secondary);
}

.v2026-cp-section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.v2026-cp-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
}
.v2026-cp-icon-btn:hover {
  background: var(--v2026-surface-hover);
}
.v2026-cp-icon-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
}

.v2026-cp-edit {
  width: 16px;
  height: 16px;
  object-fit: contain;
  display: block;
}

.v2026-cp-link {
  color: var(--v2026-link);
  text-decoration: none;
  font-size: var(--v2026-fs-body);
}
.v2026-cp-link:hover {
  text-decoration: underline;
}
.v2026-cp-link:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
  border-radius: 3px;
}

.v2026-cp-tabpane {
  display: flex;
  flex-direction: column;
  gap: var(--v2026-sp-4);
}

.v2026-cp-placeholder {
  align-items: center;
  justify-content: center;
  min-height: 160px;
  padding: var(--v2026-sp-6);
  color: var(--v2026-text-muted);
  font-size: var(--v2026-fs-body);
}

.v2026-cp-notes {
  padding: var(--v2026-sp-4) var(--v2026-sp-4) 0;
}

.v2026-cp-maidpick {
  margin-bottom: var(--v2026-sp-3);
  font-size: var(--v2026-fs-body);
}
.v2026-cp-cal__create {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--v2026-sp-2);
  width: 100%;
  margin-top: var(--v2026-sp-3);
  padding: var(--v2026-sp-3);
  border: 1px solid var(--v2026-color-main);
  border-radius: 8px;
  background: rgba(49, 193, 215, 0.08);
  color: var(--v2026-color-main);
  font-size: var(--v2026-fs-body);
  font-weight: 600;
  cursor: pointer;
}
.v2026-cp-cal__create:hover { background: rgba(49, 193, 215, 0.18); }
.v2026-cp-cal__create-ico {
  width: 18px;
  height: 18px;
  background-color: currentColor;
}

.v2026-cp-lastorder { margin-top: var(--v2026-sp-4); }
.v2026-cp-lastorder__maid {
  display: flex;
  align-items: center;
  gap: var(--v2026-sp-3);
  margin-top: var(--v2026-sp-3);
  padding-top: var(--v2026-sp-3);
  border-top: 1px solid var(--v2026-border);
}
.v2026-cp-lastorder__avatar {
  flex: 0 0 auto;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
}
.v2026-cp-lastorder__info {
  min-width: 0;
  font-size: var(--v2026-fs-meta);
  color: var(--v2026-text);
}
.v2026-cp-lastorder__gen { color: var(--v2026-text-secondary); }
.v2026-cp-lastorder__tel { color: var(--v2026-text-secondary); }
.v2026-cp-lastorder__chat {
  display: block;
  margin-top: var(--v2026-sp-3);
  padding: var(--v2026-sp-2);
  border: 1px solid var(--v2026-color-main);
  border-radius: 8px;
  background: rgba(49, 193, 215, 0.08);
  color: var(--v2026-color-main);
  font-size: var(--v2026-fs-meta);
  font-weight: 600;
  text-align: center;
  text-decoration: none;
}
.v2026-cp-lastorder__chat:hover { background: rgba(49, 193, 215, 0.18); }

.v2026-cp-bookings {
  padding: var(--v2026-sp-4) var(--v2026-sp-4) 0;
  gap: var(--v2026-sp-3);
}
/* Separates the maid calendar from the customer one above it, so a click's calendar is obvious. */
.v2026-cp-schedsec--divided {
  margin-top: var(--v2026-sp-3);
  padding-top: var(--v2026-sp-4);
  border-top: 1px solid var(--v2026-border);
}

.v2026-cp-cal {
  padding: var(--v2026-sp-3);
}
.v2026-cp-cal__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--v2026-sp-2);
}
.v2026-cp-cal__title {
  font-size: var(--v2026-fs-body);
  font-weight: 600;
  color: var(--v2026-text);
}
.v2026-cp-cal__nav {
  width: 18px;
  height: 18px;
  background-color: var(--v2026-icon);
}
.v2026-cp-cal__weekdays,
.v2026-cp-cal__grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
}
.v2026-cp-cal__weekday {
  text-align: center;
  font-size: var(--v2026-fs-meta);
  color: var(--v2026-text-muted);
  padding: var(--v2026-sp-1) 0;
}
.v2026-cp-cal__cell {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 1 / 1;
  border: 0;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  transition: background 150ms ease;
}
.v2026-cp-cal__cell--empty {
  cursor: default;
}
.v2026-cp-cal__cell:not(.v2026-cp-cal__cell--empty):hover {
  background: var(--v2026-surface-hover);
}
.v2026-cp-cal__cell:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
}
.v2026-cp-cal__num {
  font-size: var(--v2026-fs-meta);
  color: var(--v2026-text);
}

.v2026-cp-cal__cell.is-selected {
  box-shadow: inset 0 0 0 2px var(--v2026-color-main);
}
.v2026-cp-cal__cell.is-selected .v2026-cp-cal__num {
  color: var(--v2026-color-main);
  font-weight: 700;
}

.v2026-cp-cal__dots {
  position: absolute;
  bottom: 4px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 2px;
}
.v2026-cp-cal__dot {
  width: 4px;
  height: 4px;
  border-radius: 50%;
}
.v2026-cp-cal__dot--accent {
  background: var(--v2026-color-main);
}

.v2026-cp-cal__dot--more {
  width: 6px;
  height: 6px;
  background: #1a8ea3;
}
.v2026-cp-cal__dot--danger {
  background: #f04438;
}

.v2026-cp-booking {
  background: var(--v2026-surface-sunken);
  border-radius: var(--v2026-r-card);
  padding: var(--v2026-sp-3);
  display: flex;
  flex-direction: column;
  gap: var(--v2026-sp-2);
}
.v2026-cp-booking__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--v2026-sp-2);
}
.v2026-cp-booking__ids {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--v2026-sp-2);
  min-width: 0;
}
.v2026-cp-booking__id {
  font-size: var(--v2026-fs-meta);
  color: var(--v2026-text-muted);
}
.v2026-cp-booking__service {
  font-size: var(--v2026-fs-body);
  font-weight: 600;
  color: var(--v2026-text);
}
.v2026-cp-booking__row {
  display: flex;
  align-items: flex-start;
  gap: var(--v2026-sp-2);
}
.v2026-cp-booking__ico {
  width: 16px;
  height: 16px;
  flex: 0 0 auto;
  margin-top: 1px;
  background-color: var(--v2026-color-main);
}
.v2026-cp-booking__ico--danger {
  background-color: #f04438;
}
.v2026-cp-booking__text {
  font-size: var(--v2026-fs-meta);
  color: var(--v2026-text-secondary);
  line-height: 1.5;
  word-break: break-word;
}
.v2026-cp-booking__text--danger {
  color: #f04438;
}

.v2026-cp-dot {
  color: var(--v2026-text-muted);
}

.v2026-cp-important {
  padding: 0 var(--v2026-sp-4);
}

.v2026-cp-important .v2026-cp-section-label {
  font-weight: 400;
  font-size: 16px;
  line-height: 100%;
  letter-spacing: 0;
  color: #66696C;
}
.v2026-cp-important .v2026-cp-section-head .v2026-cp-edit {
  width: 18px;
  height: 18px;
}

.v2026-cp-card {
  background: var(--v2026-surface-sunken);
  border-radius: var(--v2026-r-card);
}

.v2026-cp-system {
  display: flex;
  flex-direction: column;
  gap: var(--v2026-sp-2);
  padding: 0 var(--v2026-sp-4);
}

.v2026-cp-stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--v2026-sp-2);
  padding: 0 var(--v2026-sp-4);
}
.v2026-cp-stat {
  display: flex;
  flex-direction: column;
  gap: var(--v2026-sp-1);
  padding: var(--v2026-sp-3);
}
.v2026-cp-stat--wide {
  grid-column: 1 / -1;
}
.v2026-cp-stat__top {
  display: flex;
  align-items: center;
  gap: var(--v2026-sp-2);
}
.v2026-cp-stat__ico {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  background-color: var(--v2026-color-main);
}
.v2026-cp-stat__label {
  white-space: nowrap;
  font-size: var(--v2026-fs-meta);
  color: var(--v2026-text-secondary);
}
.v2026-cp-stat__hint {
  margin-left: auto;
  font-size: 12px;
  color: var(--v2026-text-secondary);
  cursor: help;
}
.v2026-cp-stat__link {
  margin-left: auto;
  display: inline-flex;
  padding: 2px;
  border-radius: 4px;
}
.v2026-cp-stat__link:hover {
  background: var(--v2026-surface-sunken);
}
.v2026-cp-stat__link-ico {
  width: 16px;
  height: 16px;
  background-color: var(--v2026-text-secondary);
}
.v2026-cp-stat__value {
  font-size: 20px;
  font-weight: 700;
  color: var(--v2026-text);
}
/* Keeps the figure reading as a number first: no link colour or underline until
   hover, and `inline-block` so the hit area is the text itself, not the row. */
.v2026-cp-stat__value--link {
  display: inline-block;
  color: var(--v2026-text);
  text-decoration: none;
  cursor: pointer;
}
.v2026-cp-stat__value--link:hover,
.v2026-cp-stat__value--link:focus {
  color: var(--v2026-text);
  text-decoration: underline;
}
.v2026-cp-stat__requests {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--v2026-sp-1) var(--v2026-sp-2);
}
.v2026-cp-stat__request {
  font-size: var(--v2026-fs-meta);
  color: var(--v2026-link);
  text-decoration: none;
}
.v2026-cp-stat__request:hover,
.v2026-cp-stat__request:focus {
  color: var(--v2026-link);
  text-decoration: underline;
}
.v2026-cp-stat__request:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
  border-radius: 4px;
}
.v2026-cp-stat__request-more {
  font-size: var(--v2026-fs-meta);
  color: var(--v2026-text-secondary);
}
.v2026-cp-stat__value--link:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
  border-radius: 4px;
}

.v2026-cp-hint {
  font-size: 12px;
  color: var(--v2026-text-secondary);
  cursor: help;
}
.v2026-cp-hint:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
  border-radius: 50%;
}

.v2026-cp-swm__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-bottom: 1px solid #EBEDF0;
  background: #FFFFFF;
}
.v2026-cp-swm__title {
  font-size: 16px;
  font-weight: 600;
  color: #1F2937;
}
.v2026-cp-swm__close {
  padding: 0;
  border: none;
  background: transparent;
  font-size: 20px;
  line-height: 1;
  color: #66696C;
  cursor: pointer;
}
.v2026-cp-swm__body {
  padding: 20px 24px;
}
.v2026-cp-swm__route {
  display: flex;
  align-items: center;
  gap: var(--v2026-sp-2);
}
.v2026-cp-swm__acc {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  padding: var(--v2026-sp-2) var(--v2026-sp-3);
  border: 1px solid var(--v2026-border);
  border-radius: var(--v2026-r-card);
  background: var(--v2026-surface-sunken);
}
.v2026-cp-swm__acc.is-target {
  border-color: var(--v2026-color-main);
  background: var(--v2026-color-main-soft);
}
.v2026-cp-swm__acc-row {
  display: flex;
  align-items: center;
  gap: var(--v2026-sp-2);
  min-width: 0;
  margin-top: 2px;
}
.v2026-cp-swm__avatar {
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
}
.v2026-cp-swm__acc-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.v2026-cp-swm__acc-label {
  font-size: 11px;
  color: var(--v2026-text-muted);
}
.v2026-cp-swm__acc-id {
  font-size: 14px;
  font-weight: 600;
  color: var(--v2026-text);
}
.v2026-cp-swm__acc-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  color: var(--v2026-text-secondary);
}
.v2026-cp-swm__arrow {
  flex: 0 0 auto;
  color: var(--v2026-text-secondary);
}
.v2026-cp-swm__notes {
  margin: 16px 0 0;
  padding-left: 18px;
  font-size: 13px;
  line-height: 1.6;
  color: var(--v2026-text-secondary);
}
.v2026-cp-swm__error {
  margin: 12px 0 0;
  font-size: 13px;
  color: #b42318;
}
.v2026-cp-swm__actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--v2026-sp-2);
  margin-top: 20px;
}
.v2026-cp-swm__cancel,
.v2026-cp-swm__confirm {
  height: 40px;
  padding: 0 18px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}
.v2026-cp-swm__cancel {
  border: 1px solid var(--v2026-border);
  background: #FFFFFF;
  color: var(--v2026-text);
}
.v2026-cp-swm__confirm {
  border: none;
  background: var(--v2026-color-main);
  color: #FFFFFF;
}
.v2026-cp-swm__cancel:disabled,
.v2026-cp-swm__confirm:disabled {
  opacity: 0.5;
  cursor: default;
}

.v2026-cp-multi-account {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--v2026-sp-2);
  margin: var(--v2026-sp-2) var(--v2026-sp-4) 0;
  padding: var(--v2026-sp-2) var(--v2026-sp-3);
  background: var(--v2026-warning-bg);
  border: 1px solid var(--v2026-warning-bd);
  border-radius: var(--v2026-r-card);
  color: var(--v2026-warning-fg);
  font-size: 13px;
  line-height: 1.4;
}
/* Tab 1 starts flush with its header (no top padding, unlike tabs 2–4) — give it the same 16px. */
.v2026-cp-multi-account.is-above-header {
  margin-bottom: var(--v2026-sp-4);
}
.v2026-cp-multi-account__toggle {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}
.v2026-cp-multi-account__ico {
  flex-shrink: 0;
}
.v2026-cp-multi-account__count {
  padding: 0 6px;
  border-radius: 999px;
  background: var(--v2026-warning-bd);
  font-size: 11px;
  font-weight: 700;
  line-height: 16px;
}
.v2026-cp-multi-account__chev {
  flex-shrink: 0;
  transition: transform 0.15s;
}
.v2026-cp-multi-account__chev.is-open {
  transform: rotate(180deg);
}
/* ขึ้นบรรทัดใหม่เต็มความกว้างใต้หัว (flex-wrap) */
.v2026-cp-multi-account__detail {
  flex-basis: 100%;
}
.v2026-cp-multi-account__link {
  flex-shrink: 0;
  padding: 0;
  border: 0;
  background: none;
  color: var(--v2026-warning-fg);
  font-size: 13px;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
}

.v2026-cp-accounts {
  display: flex;
  flex-direction: column;
  gap: var(--v2026-sp-2);
  padding: 0 var(--v2026-sp-4);
}

.v2026-cp-accounts__list {
  background: var(--v2026-surface-sunken);
  border-radius: var(--v2026-r-card);
  overflow: hidden;
}

</style>

<style>
/* Unscoped: bootstrap-vue mounts the tooltip on <body>, outside this component's scope. */
.v2026-cp-tip .tooltip-inner {
  max-width: 320px;
  text-align: left;
  line-height: 1.6;
}
</style>
